import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import {
    IPageRequest,
    IUserKeyModel,
    SortTypes,
    UserPreferenceConstant,
    UserPreferenceService,
} from '@libs/vc-core-lib';
import { cloneDeep, isEqual, isNil } from 'lodash-es';
import { translate } from '@jsverse/transloco';
import { take } from 'rxjs';
import { ColumnDirective, NotificationService, TableComponent } from 'vcp-common-ui-lib';
import { TableScrollBinding, TableSessionStateService } from '../table-session-state/table-session-state.service';
import {
    buildLayoutUserPreferenceModel,
    buildUserPreferenceRequest,
    readFiltersFromSession,
    saveFiltersToSession,
    SearchTablePreferenceKeys,
} from './search-table-preference.util';

/** Session key for visible columns when layout user preferences are off. */
function columnsSessionStorageKey(filtersSessionStorageKey: string | undefined): string | undefined {
    return filtersSessionStorageKey ? `${filtersSessionStorageKey}:visibleColumns` : undefined;
}
/**
 * Host directive for `vc-table` that applies session scroll/filters and optional layout user preferences.
 * Use on the same host as `vc-table` (not a wrapper) so `vcColumn` templates are direct
 * content of `TableComponent` and `ContentChildren` can resolve column definitions.
 *
 * Set `[persistLayoutToUserPreferences]="false"` for session-only screens (e.g. Scan UI).
 * When false, `userKey` and layout preference keys are not required; visible columns are
 * persisted to sessionStorage under a dedicated key derived from `filtersSessionStorageKey`.
 */
@Directive({
    selector: 'vc-table[vcSearchTable]',
    standalone: true,
    exportAs: 'vcSearchTable',
})
export class SearchTableDirective<T = unknown> implements AfterViewInit, OnDestroy {
    private readonly _userPreferenceService: UserPreferenceService = inject(UserPreferenceService);
    private readonly _tableSessionStateService: TableSessionStateService = inject(TableSessionStateService);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _notificationService: NotificationService = inject(NotificationService);
    private readonly _table: TableComponent<T> = inject(TableComponent, { self: true }) as TableComponent<T>;

    private _columnSizeChangeDebouncer?: NodeJS.Timeout;
    private _pageRequestPreferenceCache: IPageRequest | null = null;
    private _isApplyingStoredColumnWidths = false;
    private _appliedVisibleColumnFields: string[] | null = null;
    private _tableScroll?: TableScrollBinding;

    visibleColumnsLoaded = false;
    visibleColumnsChanged = false;
    visibleColumns: string[] = [];

    @Input({ required: true })
    preferenceKeys!: Readonly<SearchTablePreferenceKeys>;

    /**
     * When true (default), page size/sort, columns, and widths are loaded/saved via UserPreferenceService.
     * When false, session filters/scroll and session-persisted visible columns are handled by this directive.
     */
    @Input()
    persistLayoutToUserPreferences = true;

    /** User key for persisting layout preferences. Required when `persistLayoutToUserPreferences` is true. */
    @Input()
    userKey?: IUserKeyModel;

    /** Maps stored column field names to current `vcColumn` `field` values. */
    @Input()
    normalizeStoredColumnField: (field: string) => string = (field: string) => field;

    @Input()
    defaultVisibleColumnFields!: string[];

    /** Current result page index (e.g. from `searchDTO.pageRequest.pageNumber`). */
    @Input({ required: true })
    currentPageNumber: number = 0;

    /** Current page size (e.g. from `searchDTO.pageRequest.pageSize`). */
    @Input({ required: true })
    currentPageSize: number = 25;

    /**
     * Fired after layout preference save when the table request does not match
     * `[currentPageSize]` or `[pageNumber]` inputs so the parent can sync `searchDTO.pageRequest`.
     */
    @Output()
    pageRequestToSearch = new EventEmitter<IPageRequest>();

    /**
     * Fired once on init with stored layout from preferences (page size and sort only).
     * `pageNumber` is always 0; use sessionStorage (or the parent’s session restore) for page index.
     * Emits `null` when layout preferences are disabled or nothing is stored.
     */
    @Output()
    loadedPageRequestFromPreferences = new EventEmitter<IPageRequest | null>();

    get table(): TableComponent<T> {
        return this._table;
    }

    private get _preferenceCategory(): string {
        return this.preferenceKeys.preferenceCategory ?? UserPreferenceConstant.CATEGORY_UI_ASSESSMENT;
    }

    private get _shouldPersistLayout(): boolean {
        return this.persistLayoutToUserPreferences && !!this.userKey;
    }

    /**
     * Last layout page request (page size and sort) for use when merging saved “query” filters
     * with current table layout defaults. `pageNumber` is not persisted and is always 0 here.
     */
    getLayoutPageRequestSnapshot(): IPageRequest | null {
        return this._pageRequestPreferenceCache == null ? null : cloneDeep(this._pageRequestPreferenceCache);
    }

    ngAfterViewInit(): void {
        this.visibleColumns = this.defaultVisibleColumnFields ? [...this.defaultVisibleColumnFields] : [];
        this._startTableScroll();
        this._initializeTableStateAfterViewInit();
    }

    private _initializeTableStateAfterViewInit(): void {
        this._loadPageRequestFromPreferences();
        this._loadVisibleColumnsThenWidths();
        this._changeDetectorRef.markForCheck();
    }

    private _startTableScroll(): void {
        const storageKey = this.preferenceKeys?.scrollStorageKey;
        if (!storageKey) {
            return;
        }
        this._tableScroll ??= this._tableSessionStateService.createScrollBinding({
            storageKey,
            getTable: () => this._table,
            getPageNumber: () => this.currentPageNumber,
        });
        this._tableScroll.start();
    }

    restoreTableScrollPosition(): void {
        this._tableScroll?.restore();
    }

    resetTableScrollPosition(): void {
        this._tableScroll?.reset();
    }

    saveFiltersForSession(filters: unknown): void {
        saveFiltersToSession(this.preferenceKeys.filtersSessionStorageKey, filters);
    }

    restoreFiltersForSession<TFilters = unknown>(): TFilters | null {
        return readFiltersFromSession<TFilters>(this.preferenceKeys.filtersSessionStorageKey);
    }

    /**
     * Persists layout `IPageRequest` (page size and sort only) when it differs from the last loaded/saved value.
     * No-op when `persistLayoutToUserPreferences` is false.
     * Page index is not written to the user preference service; it is kept in session (e.g. with search filters).
     */
    savePageRequestPreferenceIfChanged(pageRequest: IPageRequest): void {
        if (!this._shouldPersistLayout || !this.preferenceKeys.pageRequestPreferenceKey || !this.userKey) {
            return;
        }
        if (this._pageRequestPreferenceCache != null && isEqual(pageRequest, this._pageRequestPreferenceCache)) {
            return;
        }
        this._userPreferenceService
            .setUserPreference(
                buildLayoutUserPreferenceModel(
                    this.userKey,
                    this.preferenceKeys.pageRequestPreferenceKey,
                    pageRequest,
                    UserPreferenceConstant.SUBCATEGORY_LAYOUT,
                    this._preferenceCategory
                )
            )
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this._pageRequestPreferenceCache = cloneDeep(pageRequest);
                    this.pageRequestToSearch.emit(pageRequest);
                    if (this.currentPageSize !== pageRequest.pageSize) {
                        this._notificationService.success(
                            translate('USER.PREFERENCE.PAGE_SIZE_PREFERENCE_SAVED_SUCCESSFULLY')
                        );
                    }
                },
            });
    }

    onVisibleColumnsChange(columns: ColumnDirective[]): void {
        if (!this.visibleColumnsLoaded) {
            return;
        }
        const fields = columns.map((column) => column.field);
        // The column selector emits on every apply, even when the selection is unchanged. Without this
        // check, `visibleColumnsChanged` stays set and suppresses the next search the user triggers.
        if (isEqual(fields, this._appliedVisibleColumnFields)) {
            return;
        }
        this._appliedVisibleColumnFields = [...fields];
        this.visibleColumns = [...fields];
        this.visibleColumnsChanged = true;

        if (this._shouldPersistLayout && this.preferenceKeys.columnsPreferenceKey && this.userKey) {
            this._userPreferenceService
                .setUserPreference(
                    buildLayoutUserPreferenceModel(
                        this.userKey,
                        this.preferenceKeys.columnsPreferenceKey,
                        fields,
                        UserPreferenceConstant.SUBCATEGORY_LAYOUT,
                        this._preferenceCategory
                    )
                )
                .pipe(take(1))
                .subscribe({
                    error: () => {
                        this._notificationService.error(translate('TASK.SEARCH.SAVE_SELECTED_COLUMN_PREFERENCE_ERROR'));
                    },
                });
            return;
        }

        if (!this._shouldPersistLayout) {
            saveFiltersToSession(columnsSessionStorageKey(this.preferenceKeys.filtersSessionStorageKey), fields);
        }
    }

    onColumnSizeChange(_column: ColumnDirective): void {
        if (!this.visibleColumnsLoaded || this._isApplyingStoredColumnWidths || !this._shouldPersistLayout) {
            return;
        }

        this._updateTableMinWidth();

        if (this._columnSizeChangeDebouncer !== undefined) {
            clearTimeout(this._columnSizeChangeDebouncer);
        }
        this._columnSizeChangeDebouncer = setTimeout(() => {
            this.saveColumnWidthsToUserPreferences();
        }, 500);
    }

    acknowledgeVisibleColumnsChange(): void {
        this.visibleColumnsChanged = false;
    }

    saveColumnWidthsToUserPreferences(): void {
        if (!this._shouldPersistLayout || !this.preferenceKeys.tablePreferenceKey || !this.userKey) {
            return;
        }

        const entries = this.collectTableColumnWidthMap();
        if (entries.size === 0) {
            return;
        }

        this._userPreferenceService
            .setUserPreference(
                buildLayoutUserPreferenceModel(
                    this.userKey,
                    this.preferenceKeys.tablePreferenceKey,
                    Array.from(entries.entries()),
                    UserPreferenceConstant.SUBCATEGORY_LAYOUT,
                    this._preferenceCategory
                )
            )
            .pipe(take(1))
            .subscribe();
    }

    collectTableColumnWidthMap(): Map<string, string> {
        const columns = new Map<string, string>();
        this._table?.columnsList.forEach((column: ColumnDirective) => {
            if (column.field && column.width) {
                columns.set(column.field, column.width);
            }
        });
        return columns;
    }

    private _loadPageRequestFromPreferences(): void {
        if (!this._shouldPersistLayout || !this.preferenceKeys.pageRequestPreferenceKey) {
            this._pageRequestPreferenceCache = null;
            this.loadedPageRequestFromPreferences.emit(null);
            return;
        }

        const pageRequestFromKey = this._readPageRequestFromKey(this.preferenceKeys.pageRequestPreferenceKey);
        if (pageRequestFromKey) {
            this._pageRequestPreferenceCache = cloneDeep(pageRequestFromKey);
            this.loadedPageRequestFromPreferences.emit(pageRequestFromKey);
            return;
        }
        this._pageRequestPreferenceCache = null;
        this.loadedPageRequestFromPreferences.emit(null);
    }

    private _readPageRequestFromKey(attributeKey: string): IPageRequest | null {
        try {
            const raw = this._userPreferenceService.getUserPreference(
                buildUserPreferenceRequest(
                    UserPreferenceConstant.SUBCATEGORY_LAYOUT,
                    attributeKey,
                    this._preferenceCategory
                )
            ) as string;
            if (raw == null || raw === '') {
                return null;
            }
            const parsed = JSON.parse(raw) as IPageRequest;
            return this._parseStoredLayoutPagePreferencePayload(parsed);
        } catch {
            return null;
        }
    }

    /**
     * Reads layout from user preferences: page size and sort. Legacy values may include `pageNumber`;
     * it is ignored. Page index is not restored from the preference service.
     */
    private _parseStoredLayoutPagePreferencePayload(pageRequest: IPageRequest): IPageRequest | null {
        if (pageRequest == null || typeof pageRequest !== 'object') {
            return null;
        }
        const pageSize = pageRequest.pageSize;
        if (typeof pageSize !== 'number' || pageSize <= 0) {
            return null;
        }
        if (!Array.isArray(pageRequest.sort)) {
            return null;
        }
        for (const sortField of pageRequest.sort) {
            if (typeof sortField?.fieldName !== 'string' || sortField.fieldName.length === 0) {
                return null;
            }
            if (sortField.sort !== SortTypes.ASC && sortField.sort !== SortTypes.DESC) {
                return null;
            }
        }
        return { pageSize, pageNumber: 0, sort: pageRequest.sort };
    }

    private _loadVisibleColumnsThenWidths(): void {
        let visible: string[] | undefined;

        if (this._shouldPersistLayout && this.preferenceKeys.columnsPreferenceKey) {
            try {
                visible = JSON.parse(
                    this._userPreferenceService.getUserPreference(
                        buildUserPreferenceRequest(
                            UserPreferenceConstant.SUBCATEGORY_LAYOUT,
                            this.preferenceKeys.columnsPreferenceKey,
                            this._preferenceCategory
                        )
                    ) as string
                );
            } catch {
                visible = undefined;
            }
        } else if (!this._shouldPersistLayout) {
            visible = this._readSessionVisibleColumns();
        }

        if (!isNil(visible)) {
            this.visibleColumns = visible.map((col) => this.normalizeStoredColumnField(col));
        }

        setTimeout(() => {
            this._table.setVisibleColumnByFields(this.visibleColumns);
            this._appliedVisibleColumnFields = this._table.visibleColumnList.map((column) => column.field);
            this.visibleColumnsLoaded = true;

            // Load the widths AFTER the visible columns are loaded (user-pref mode only)
            this._loadColumnWidthsFromPreferences();
        }, 0);
    }

    private _readSessionVisibleColumns(): string[] | undefined {
        const fromColumnsKey = readFiltersFromSession<string[]>(
            columnsSessionStorageKey(this.preferenceKeys.filtersSessionStorageKey)
        );
        if (Array.isArray(fromColumnsKey) && fromColumnsKey.length > 0) {
            return fromColumnsKey;
        }
        return undefined;
    }

    // Helper method to parse a stored width string (e.g. "100px") into number of pixels.
    private _parseStoredWidthPx(width: string): number | null {
        const match = /^([\d.]+)px$/i.exec(width.trim());
        return match ? Number.parseFloat(match[1]) : null;
    }

    // Helper method to sum up the pixel widths for visible columns.
    private _sumVisibleColumnWidthsPx(columnsMap?: Map<string, string>): number {
        let totalPx = 0;

        for (const column of this._table.columnsList) {
            if (!column.visible) {
                continue;
            }
            const width = columnsMap?.get(column.field) ?? column.width;
            const px = width ? this._parseStoredWidthPx(width) : null;
            totalPx += px ?? column.minWidth ?? 100;
        }

        return totalPx;
    }

    // Helper method to update the minimum width of the table in case the widths surpass the parent container width
    private _updateTableMinWidth(columnsMap?: Map<string, string>): void {
        const tableContainer = this._table?.tableContainer?.nativeElement;
        if (!(tableContainer instanceof HTMLElement)) {
            return;
        }

        const table = tableContainer.querySelector('table');
        if (!(table instanceof HTMLElement)) {
            return;
        }

        const totalPx = this._sumVisibleColumnWidthsPx(columnsMap);
        if (totalPx > 0) {
            table.style.minWidth = `${totalPx}px`;
        }
    }

    private _loadColumnWidthsFromPreferences(): void {
        if (!this._shouldPersistLayout) {
            return;
        }
        requestAnimationFrame(() => this._applyStoredColumnWidths());
    }

    private _applyStoredColumnWidths(): void {
        if (!this.preferenceKeys.tablePreferenceKey) {
            return;
        }

        let parsedColumnWidths: [string, string][];
        try {
            parsedColumnWidths = JSON.parse(
                this._userPreferenceService.getUserPreference(
                    buildUserPreferenceRequest(
                        UserPreferenceConstant.SUBCATEGORY_LAYOUT,
                        this.preferenceKeys.tablePreferenceKey,
                        this._preferenceCategory
                    )
                ) as string
            );
        } catch {
            return;
        }

        const columnsMap: Map<string, string> = new Map<string, string>(parsedColumnWidths);

        if (columnsMap.size === 0 || !this._table?.columnsList?.length) {
            return;
        }

        this._isApplyingStoredColumnWidths = true;

        this._table.columnsList.forEach((column: ColumnDirective) => {
            if (columnsMap.has(column.field)) {
                column.width = columnsMap.get(column.field) ?? '';
            }
        });

        this._updateTableMinWidth(columnsMap);

        this._changeDetectorRef.markForCheck();

        setTimeout(() => {
            this._isApplyingStoredColumnWidths = false;
        }, 0);
    }

    ngOnDestroy(): void {
        if (this._columnSizeChangeDebouncer !== undefined) {
            clearTimeout(this._columnSizeChangeDebouncer);
        }
        this._tableScroll?.destroy();
        this._tableScroll = undefined;
    }
}
